import { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, X, ScanLine } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

export default function BarcodeScannerModal({ open, onClose, onScan }) {
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const containerRef = useRef(null);

  const startScanner = useCallback(async () => {
    if (!containerRef.current || scannerRef.current) return;
    setError(null);
    setScanning(true);

    try {
      const scanner = new Html5Qrcode('barcode-scanner-region');
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 280, height: 120 },
          aspectRatio: 1.5,
          formatsToSupport: [0, 2, 4, 7, 8, 10, 11, 13] // QR, CODE128, CODE39, EAN, ITF, etc.
        },
        (decodedText) => {
          // Barcode detected!
          onScan(decodedText.trim());
          stopScanner();
          onClose();
        },
        () => {} // ignore scan failures (no code in frame)
      );
    } catch (err) {
      setError('No se pudo acceder a la cámara. Verifica los permisos.');
      setScanning(false);
    }
  }, [onScan, onClose]);

  const stopScanner = useCallback(() => {
    if (scannerRef.current) {
      try {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current.clear();
      } catch { /* ignore */ }
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  useEffect(() => {
    if (open) {
      // Small delay to ensure the DOM element is rendered
      const timer = setTimeout(() => startScanner(), 300);
      return () => clearTimeout(timer);
    } else {
      stopScanner();
    }
    return () => stopScanner();
  }, [open, startScanner, stopScanner]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { stopScanner(); onClose(); } }}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="barcode-scanner-modal">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Camera className="w-5 h-5 text-orange-500" />
            Escanear Código
          </DialogTitle>
        </DialogHeader>

        <div className="relative">
          {/* Scanner viewport */}
          <div 
            id="barcode-scanner-region" 
            ref={containerRef}
            className="w-full bg-black"
            style={{ minHeight: 300 }}
            data-testid="scanner-viewport"
          />

          {/* Scanning overlay */}
          {scanning && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="border-2 border-orange-400 rounded-lg animate-pulse"
                style={{ width: 280, height: 120 }} />
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80">
              <div className="text-center p-4">
                <Camera className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                <p className="text-white text-sm mb-3">{error}</p>
                <Button size="sm" variant="outline" onClick={startScanner}
                  className="text-white border-white hover:bg-white/20">
                  Reintentar
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 pb-4 pt-2 text-center">
          <p className="text-xs text-slate-500 mb-3">
            Apunta la cámara al código de barras o QR de la bicicleta
          </p>
          <Button variant="outline" onClick={() => { stopScanner(); onClose(); }} className="w-full"
            data-testid="close-scanner-btn">
            <X className="w-4 h-4 mr-2" /> Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
